import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Sparkles, Play, MoreVertical, LayoutGrid, List } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../components/ui/Select';
import { Checkbox } from '../../components/ui/Checkbox';
import { Badge } from '../../components/ui/Badge';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '../../components/ui/DropdownMenu';
import { freesoundCollections, freesoundSounds } from '../../src/seed';
export function FreesoundCollectionDetail() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const collection = freesoundCollections.find(c => c.id === id);
  const [selectedSounds, setSelectedSounds] = useState<Set<string>>(new Set());
  if (!collection) {
    return <Card data-id="element-127">
        <CardHeader data-id="element-128">
          <CardTitle data-id="element-129">Collection not found</CardTitle>
          <CardDescription data-id="element-130">
            The collection you are looking for does not exist.
          </CardDescription>
        </CardHeader>
      </Card>;
  }
  const sounds = freesoundSounds.filter(s => s.collectionIds.includes(collection.id));
  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };
  return <div className="space-y-6 pb-20" data-id="element-131">
      <div className="flex items-start justify-between" data-id="element-132">
        <div data-id="element-133">
          <div className="flex items-center gap-3 mb-2" data-id="element-134">
            <h1 className="text-3xl font-bold tracking-tight" data-id="element-135">
              {collection.name}
            </h1>
            <Badge variant="secondary" className="mt-1" data-id="element-136">
              Manual collection
            </Badge>
          </div>
          <p className="text-muted-foreground" data-id="element-137">{collection.itemCount} sounds</p>
        </div>
        <div className="flex gap-2" data-id="element-138">
          <Button variant="outline" data-id="element-139">Edit collection</Button>
          <Button data-id="element-140">
            <Sparkles className="mr-2 h-4 w-4" data-id="element-141" />
            Add sounds
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-4" data-id="element-142">
        <Input placeholder="Filter this collection..." className="max-w-sm" data-id="element-143" />
        <Select defaultValue="newest" data-id="element-144">
          <SelectTrigger className="w-[180px]" data-id="element-145">
            <SelectValue placeholder="Sort by" data-id="element-146" />
          </SelectTrigger>
          <SelectContent data-id="element-147">
            <SelectItem value="newest" data-id="element-148">Newest first</SelectItem>
            <SelectItem value="oldest" data-id="element-149">Oldest</SelectItem>
            <SelectItem value="az" data-id="element-150">Name A-Z</SelectItem>
            <SelectItem value="duration" data-id="element-151">Duration</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto flex gap-1" data-id="element-152">
          <Button variant="ghost" size="icon" data-id="element-153">
            <List className="h-4 w-4" data-id="element-154" />
          </Button>
          <Button variant="ghost" size="icon" className="text-muted-foreground" data-id="element-155">
            <LayoutGrid className="h-4 w-4" data-id="element-156" />
          </Button>
        </div>
      </div>

      <div className="space-y-2" data-id="element-157">
        {sounds.map(sound => <Card key={sound.id} className="cursor-pointer hover:bg-muted/50 transition-colors" onClick={() => navigate(`/freesound/library/${sound.id}`)} data-id="element-158">
            <div className="flex items-center p-4 gap-4" data-id="element-159">
              <div onClick={e => e.stopPropagation()} data-id="element-160">
                <Checkbox checked={selectedSounds.has(sound.id)} onCheckedChange={checked => {
              const newSet = new Set(selectedSounds);
              if (checked) newSet.add(sound.id);else newSet.delete(sound.id);
              setSelectedSounds(newSet);
            }} data-id="element-161" />
              </div>
              <Button variant="ghost" size="icon" className="shrink-0 rounded-full" onClick={e => e.stopPropagation()} data-id="element-162">
                <Play className="h-4 w-4" data-id="element-163" />
              </Button>
              <div className="flex-1 min-w-0" data-id="element-164">
                <h3 className="font-medium truncate" data-id="element-165">{sound.name}</h3>
                <p className="text-sm text-muted-foreground truncate" data-id="element-166">
                  by {sound.creator} · {formatDuration(sound.duration)} ·{' '}
                  {sound.type.toUpperCase()} · {sound.license}
                </p>
              </div>
              <div className="hidden md:flex items-center gap-2" data-id="element-167">
                {sound.tags.slice(0, 4).map(tag => <Badge key={tag} variant="outline" data-id="element-168">
                    {tag}
                  </Badge>)}
              </div>
              <DropdownMenu data-id="element-169">
                <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()} data-id="element-170">
                  <Button variant="ghost" size="icon" data-id="element-171">
                    <MoreVertical className="h-4 w-4" data-id="element-172" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" data-id="element-173">
                  <DropdownMenuItem onClick={() => navigate(`/freesound/library/${sound.id}`)} data-id="element-174">
                    Open
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={e => e.stopPropagation()} data-id="element-175">
                    Move to collection...
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={e => e.stopPropagation()} className="text-destructive" data-id="element-176">
                    Remove from collection
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </Card>)}
        {sounds.length === 0 && <div className="text-center py-12 text-muted-foreground border-2 border-dashed rounded-lg" data-id="element-177">
            No sounds in this collection yet.
          </div>}
      </div>

      {selectedSounds.size > 0 && <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-background border shadow-lg rounded-full px-6 py-3 flex items-center gap-4 animate-in slide-in-from-bottom-10" data-id="element-178">
          <span className="text-sm font-medium" data-id="element-179">
            {selectedSounds.size} selected
          </span>
          <div className="h-4 w-px bg-border" data-id="element-180" />
          <Button variant="ghost" size="sm" onClick={() => setSelectedSounds(new Set())} data-id="element-181">
            Clear
          </Button>
          <Button variant="outline" size="sm" data-id="element-182">
            Move to...
          </Button>
          <Button variant="outline" size="sm" className="text-destructive border-destructive/20 hover:bg-destructive/10" data-id="element-183">
            Remove from collection
          </Button>
        </div>}
    </div>;
}